/**
 * cdn-domain-waf-analytics-tab.tsx — WAF Analytics tab for a CDN domain (act-as).
 *
 * Read-only: requests vs blocked time-series chart, top threats / blocked-IPs / rule IDs.
 * Uses @vnetwork/ui/charts Chart component (matches codebase pattern).
 *
 * Endpoints: GET /api/v3/cdn/domains/:id/waf-analytics?from=&to=
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart2, RefreshCw } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNWafAnalytics,
  type AnalyticsDataPoint,
  type AnalyticsTopEvent,
} from '@/api/act-as-cdn-security';

/* ── Date range helpers ──────────────────────────────────────────────────── */

function isoOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const RANGES = [
  { value: '1',  label: 'Last 24 h' },
  { value: '7',  label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
] as const;

/* ── Palette (Muted Material 400) ────────────────────────────────────────── */
const COLOR_REQUESTS = CHART_COLORS[0]; // blue — primary traffic
const COLOR_BLOCKED  = CHART_COLORS[4]; // red — blocked

/* ── Mini stat card ──────────────────────────────────────────────────────── */
function StatCard({ label, value, tone }: { label: string; value: number | undefined; tone: 'info' | 'danger' | 'warning' }) {
  return (
    <Card>
      <CardBody style={{ textAlign: 'center', padding: 'var(--vn-space-4)' }}>
        <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700, color: `var(--vn-${tone}-text)` }}>
          {value?.toLocaleString() ?? '—'}
        </div>
        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 4 }}>{label}</div>
      </CardBody>
    </Card>
  );
}

/* ── Top events table ────────────────────────────────────────────────────── */
const TOP_EVENT_COLUMNS = (labelHeader: string): ColumnDef<AnalyticsTopEvent, unknown>[] => [
  { id: 'label', accessorKey: 'label', header: labelHeader, cell: ({ row }) => <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{row.original.label}</span> },
  { id: 'count', accessorKey: 'count', header: 'Count', cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{row.original.count.toLocaleString()}</span> },
  { id: 'pct', accessorKey: 'pct', header: '%', cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right', color: 'var(--vn-fg-muted)' }}>{row.original.pct != null ? `${row.original.pct.toFixed(1)}%` : '—'}</span> },
];

function TopTable({ title, rows, labelHeader }: { title: string; rows: AnalyticsTopEvent[]; labelHeader: string }) {
  if (!rows.length) return null;
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable columns={TOP_EVENT_COLUMNS(labelHeader)} data={rows} dense />
      </CardBody>
    </Card>
  );
}

/* ── Chart data normaliser ───────────────────────────────────────────────── */
function mergeTimeSeries(
  requests: AnalyticsDataPoint[] = [],
  blocked: AnalyticsDataPoint[] = [],
): { ts: string; requests: number; blocked: number }[] {
  const map = new Map<string, { requests: number; blocked: number }>();
  for (const p of requests) map.set(p.ts, { requests: p.value, blocked: 0 });
  for (const p of blocked) {
    const row = map.get(p.ts) ?? { requests: 0, blocked: 0 };
    map.set(p.ts, { ...row, blocked: p.value });
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ts, v]) => ({ ts, ...v }));
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface Props { domainId: string }

export function CdnDomainWafAnalyticsTab({ domainId }: Props) {
  const { t } = useTranslation();
  const [days, setDays] = useState<string>('7');

  const from = isoOffset(Number(days));
  const to   = isoOffset(0);

  const q = useActAsCDNWafAnalytics(domainId, from, to);

  if (q.isLoading) return <Skeleton style={{ height: 380 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.waf_analytics_tab.load_error', 'Failed to load WAF analytics')}
        onRetry={() => q.refetch()}
      />
    );
  }

  const data      = q.data;
  const chartData = mergeTimeSeries(data?.requests_series, data?.blocked_series);

  return (
    <Section
      title={t('cdn_domain.waf_analytics_tab.title', 'WAF Analytics')}
      description={t('cdn_domain.waf_analytics_tab.description', 'Request, block, and threat counts over time. Read-only.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* Range selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', justifyContent: 'flex-end' }}>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger style={{ width: 140 }}><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" startIcon={<RefreshCw size={14} />} onClick={() => q.refetch()}>
            {t('cdn_domain.waf_analytics_tab.refresh', 'Refresh')}
          </Button>
        </div>

        {/* Summary stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
          <StatCard label={t('cdn_domain.waf_analytics_tab.stat_requests', 'Total requests')} value={data?.total_requests} tone="info" />
          <StatCard label={t('cdn_domain.waf_analytics_tab.stat_blocked', 'Blocked')} value={data?.blocked_requests} tone="danger" />
          <StatCard label={t('cdn_domain.waf_analytics_tab.stat_threats', 'Threats detected')} value={data?.threats_detected} tone="warning" />
        </div>

        {/* Time-series chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.waf_analytics_tab.chart_title', 'Requests vs Blocked')}</CardTitle>
            <CardDescription>{t('cdn_domain.waf_analytics_tab.chart_desc', 'Traffic volume over the selected period.')}</CardDescription>
          </CardHeader>
          <CardBody>
            {chartData.length > 0 ? (
              <Chart
                type="area"
                height={240}
                data={chartData}
                xKey="ts"
                series={[
                  { key: 'requests', label: 'Requests', color: COLOR_REQUESTS },
                  { key: 'blocked',  label: 'Blocked',  color: COLOR_BLOCKED },
                ]}
                showLegend
                showGrid
                valueFormatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))}
              />
            ) : (
              <EmptyState
                size="sm"
                icon={<BarChart2 size={20} />}
                title={t('cdn_domain.waf_analytics_tab.no_data', 'No data for this period')}
              />
            )}
          </CardBody>
        </Card>

        {/* Top tables */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--vn-space-4)' }}>
          <TopTable
            title={t('cdn_domain.waf_analytics_tab.top_threats', 'Top Threat Types')}
            rows={data?.top_threats ?? []}
            labelHeader={t('cdn_domain.waf_analytics_tab.col_threat', 'Threat')}
          />
          <TopTable
            title={t('cdn_domain.waf_analytics_tab.top_blocked_ips', 'Top Blocked IPs')}
            rows={data?.top_blocked_ips ?? []}
            labelHeader="IP"
          />
          <TopTable
            title={t('cdn_domain.waf_analytics_tab.top_rules', 'Top Triggered Rules')}
            rows={data?.top_rule_ids ?? []}
            labelHeader={t('cdn_domain.waf_analytics_tab.col_rule', 'Rule ID')}
          />
        </div>

      </div>
    </Section>
  );
}
